// 日本語あいまい検索ユーティリティ
// - カタカナ ⇔ ひらがな を正規化（同一視）
// - 全角英数 → 半角に正規化
// - 大文字小文字を無視
// - 半角カナ → 全角カナ に正規化（一般的なものに限定）
// 漢字は読みデータが無いため変換できないが、商品名そのまま入力できる前提で、
// ひらがな/カタカナの相互変換と全/半角の正規化で実用上の「あいまい検索」を実現する。

const HALFWIDTH_KANA_MAP = {
  "ｱ": "ア", "ｲ": "イ", "ｳ": "ウ", "ｴ": "エ", "ｵ": "オ",
  "ｶ": "カ", "ｷ": "キ", "ｸ": "ク", "ｹ": "ケ", "ｺ": "コ",
  "ｻ": "サ", "ｼ": "シ", "ｽ": "ス", "ｾ": "セ", "ｿ": "ソ",
  "ﾀ": "タ", "ﾁ": "チ", "ﾂ": "ツ", "ﾃ": "テ", "ﾄ": "ト",
  "ﾅ": "ナ", "ﾆ": "ニ", "ﾇ": "ヌ", "ﾈ": "ネ", "ﾉ": "ノ",
  "ﾊ": "ハ", "ﾋ": "ヒ", "ﾌ": "フ", "ﾍ": "ヘ", "ﾎ": "ホ",
  "ﾏ": "マ", "ﾐ": "ミ", "ﾑ": "ム", "ﾒ": "メ", "ﾓ": "モ",
  "ﾔ": "ヤ", "ﾕ": "ユ", "ﾖ": "ヨ",
  "ﾗ": "ラ", "ﾘ": "リ", "ﾙ": "ル", "ﾚ": "レ", "ﾛ": "ロ",
  "ﾜ": "ワ", "ｦ": "ヲ", "ﾝ": "ン",
  "ｧ": "ァ", "ｨ": "ィ", "ｩ": "ゥ", "ｪ": "ェ", "ｫ": "ォ",
  "ｬ": "ャ", "ｭ": "ュ", "ｮ": "ョ", "ｯ": "ッ", "ｰ": "ー",
};

const HALFWIDTH_DAKUTEN_MAP = {
  "ｶﾞ": "ガ", "ｷﾞ": "ギ", "ｸﾞ": "グ", "ｹﾞ": "ゲ", "ｺﾞ": "ゴ",
  "ｻﾞ": "ザ", "ｼﾞ": "ジ", "ｽﾞ": "ズ", "ｾﾞ": "ゼ", "ｿﾞ": "ゾ",
  "ﾀﾞ": "ダ", "ﾁﾞ": "ヂ", "ﾂﾞ": "ヅ", "ﾃﾞ": "デ", "ﾄﾞ": "ド",
  "ﾊﾞ": "バ", "ﾋﾞ": "ビ", "ﾌﾞ": "ブ", "ﾍﾞ": "ベ", "ﾎﾞ": "ボ",
  "ﾊﾟ": "パ", "ﾋﾟ": "ピ", "ﾌﾟ": "プ", "ﾍﾟ": "ペ", "ﾎﾟ": "ポ",
  "ｳﾞ": "ヴ",
};

// 全角英数 → 半角
const toHalfWidthAlnum = (s) =>
  s.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (c) =>
    String.fromCharCode(c.charCodeAt(0) - 0xFEE0)
  );

// 半角カナ → 全角カナ（濁点・半濁点合成含む）
const halfKanaToFull = (s) => {
  let out = s;
  for (const [k, v] of Object.entries(HALFWIDTH_DAKUTEN_MAP)) {
    out = out.split(k).join(v);
  }
  for (const [k, v] of Object.entries(HALFWIDTH_KANA_MAP)) {
    out = out.split(k).join(v);
  }
  return out;
};

// ひらがな → カタカナ
const hiraToKata = (s) =>
  s.replace(/[\u3041-\u3096]/g, (c) =>
    String.fromCharCode(c.charCodeAt(0) + 0x60)
  );

export const normalizeJa = (s) => {
  if (!s) return "";
  let v = String(s);
  v = toHalfWidthAlnum(v);
  v = halfKanaToFull(v);
  v = hiraToKata(v);
  return v.toLowerCase();
};

// ローマ字 → カタカナ（英字入力のままでも「negi」で「ねぎ」が見つかるように）
const ROMA = {
  kya: "キャ", kyu: "キュ", kyo: "キョ", sha: "シャ", shu: "シュ", sho: "ショ", she: "シェ", shi: "シ",
  sya: "シャ", syu: "シュ", syo: "ショ", cha: "チャ", chu: "チュ", cho: "チョ", che: "チェ", chi: "チ",
  tya: "チャ", tyu: "チュ", tyo: "チョ", tsu: "ツ", nya: "ニャ", nyu: "ニュ", nyo: "ニョ",
  hya: "ヒャ", hyu: "ヒュ", hyo: "ヒョ", mya: "ミャ", myu: "ミュ", myo: "ミョ", rya: "リャ", ryu: "リュ", ryo: "リョ",
  gya: "ギャ", gyu: "ギュ", gyo: "ギョ", jya: "ジャ", jyu: "ジュ", jyo: "ジョ", bya: "ビャ", byu: "ビュ", byo: "ビョ",
  pya: "ピャ", pyu: "ピュ", pyo: "ピョ", dya: "ヂャ", dyu: "ヂュ", dyo: "ヂョ",
  ja: "ジャ", ju: "ジュ", jo: "ジョ", je: "ジェ", ji: "ジ", fa: "ファ", fi: "フィ", fe: "フェ", fo: "フォ", fu: "フ",
  ka: "カ", ki: "キ", ku: "ク", ke: "ケ", ko: "コ", sa: "サ", si: "シ", su: "ス", se: "セ", so: "ソ",
  ta: "タ", ti: "チ", tu: "ツ", te: "テ", to: "ト", na: "ナ", ni: "ニ", nu: "ヌ", ne: "ネ", no: "ノ",
  ha: "ハ", hi: "ヒ", hu: "フ", he: "ヘ", ho: "ホ", ma: "マ", mi: "ミ", mu: "ム", me: "メ", mo: "モ",
  ya: "ヤ", yu: "ユ", yo: "ヨ", ra: "ラ", ri: "リ", ru: "ル", re: "レ", ro: "ロ", la: "ラ", li: "リ", lu: "ル", le: "レ", lo: "ロ",
  wa: "ワ", wo: "ヲ", ga: "ガ", gi: "ギ", gu: "グ", ge: "ゲ", go: "ゴ", za: "ザ", zi: "ジ", zu: "ズ", ze: "ゼ", zo: "ゾ",
  da: "ダ", di: "ヂ", du: "ヅ", de: "デ", do: "ド", ba: "バ", bi: "ビ", bu: "ブ", be: "ベ", bo: "ボ",
  pa: "パ", pi: "ピ", pu: "プ", pe: "ペ", po: "ポ", va: "ヴァ", vi: "ヴィ", vu: "ヴ", ve: "ヴェ", vo: "ヴォ",
  nn: "ン", "n'": "ン", a: "ア", i: "イ", u: "ウ", e: "エ", o: "オ", "-": "ー",
};

// 変換できない文字が残れば null（入力途中の末尾子音は無視して前方一致させる）
export const romajiToKana = (input) => {
  const s = input.toLowerCase();
  let out = "";
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c === " ") { i += 1; continue; }
    if (i + 1 < s.length && c === s[i + 1] && /[bcdfghjkmpqrstvwxyz]/.test(c)) { out += "ッ"; i += 1; continue; }
    let hit = false;
    for (const len of [3, 2, 1]) {
      const k = s.substr(i, len);
      if (ROMA[k]) { out += ROMA[k]; i += len; hit = true; break; }
    }
    if (hit) continue;
    if (c === "n" && (i + 1 >= s.length || !/[aiueoy]/.test(s[i + 1]))) { out += "ン"; i += 1; continue; }
    if (i >= s.length - 2 && /[a-z]/.test(c) && /^[a-z]*$/.test(s.slice(i))) break; // 入力途中
    return null;
  }
  return out || null;
};

// 検索: クエリと対象テキスト群（複数）を正規化してマッチ判定
export const fuzzyMatchJa = (query, ...candidates) => {
  const q = normalizeJa(query).trim();
  if (!q) return true;
  const kana = /^[a-z' -]{2,}$/.test(q) ? romajiToKana(q) : null;
  for (const c of candidates) {
    if (!c) continue;
    const n = normalizeJa(c);
    if (n.includes(q) || (kana && n.includes(kana))) return true;
  }
  return false;
};
